import { useRef } from 'react';
import { Star } from 'lucide-react';
import type { PhotoDto, PhotoRequest } from '../types/apartment';
import { usePhotoCarousel } from '../hooks/usePhotoCarousel';
import { useArrowAutoScroll } from '../hooks/useArrowAutoScroll';
import { PhotoCarousel } from './PhotoCarousel';
import { PhotoImg } from './PhotoImg';

interface PhotoGalleryEditorProps {
  apartmentId: string;
  photos: PhotoDto[];
  onPhotosChange: (photos: PhotoRequest[]) => void;
  /** Called when the user clicks the delete button on a thumbnail.
   *  The parent (ApartmentForm) owns the delete mutation and confirmation logic. */
  onDeletePhoto?: (photo: PhotoDto) => void;
}

/**
 * Top-level photo gallery management component.
 *
 * Layout:
 *   ┌─────────────────────────────────────┐
 *   │  Hero slot (primary photo, large)   │
 *   └─────────────────────────────────────┘
 *   ← [thumb][thumb][thumb][thumb][thumb] →   ← carousel
 *
 * The primary photo (displayOrder === 0) is rendered large in the hero slot.
 * All photos (including primary) appear as thumbnails in the sortable carousel.
 * Drag-and-drop reordering and "Mark as Primary" both call onPhotosChange after
 * recalculating displayOrder values — the parent is responsible for persistence.
 */
export function PhotoGalleryEditor({ photos, onPhotosChange, onDeletePhoto }: PhotoGalleryEditorProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { photos: sortedPhotos, activeId, handleDragStart, handleDragEnd, handleDragCancel, handleMarkAsPrimary } =
    usePhotoCarousel({ initialPhotos: photos, onOrderChange: onPhotosChange });

  const { leftArrowRef, rightArrowRef } = useArrowAutoScroll({
    scrollContainerRef,
    isDragging: activeId !== null,
  });

  const primaryPhoto = sortedPhotos[0] ?? null;
  const isDragging = activeId !== null;

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 rounded-xl border-2 border-dashed border-gray-200 text-gray-400">
        <p className="text-sm">No photos yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Hero slot ──────────────────────────────────────────────────── */}
      {primaryPhoto && (
        <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video">
          <PhotoImg
            src={primaryPhoto.optimizedUrl ?? primaryPhoto.photoUrl}
            fallbackSrc={primaryPhoto.photoUrl}
            alt="Primary apartment photo"
            className="w-full h-full object-cover"
          />
          {/* Primary badge on the hero */}
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded-full">
            <Star size={10} fill="currentColor" />
            <span>Primary</span>
          </div>
        </div>
      )}

      {/* ── Thumbnail carousel ─────────────────────────────────────────── */}
      {sortedPhotos.length > 1 && (
        <PhotoCarousel
          photos={sortedPhotos}
          activeId={activeId}
          isDragging={isDragging}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          onMarkAsPrimary={handleMarkAsPrimary}
          onDeletePhoto={(key) => {
            const photo = sortedPhotos.find(p => p.key === key);
            if (photo && onDeletePhoto) onDeletePhoto(photo);
          }}
          scrollContainerRef={scrollContainerRef}
          leftArrowRef={leftArrowRef}
          rightArrowRef={rightArrowRef}
        />
      )}
    </div>
  );
}
